/**
 * Collections Page — Elvoaq Platform
 * Two distinct views inside the detail modal:
 *   1. "Items" tab — shows items already in the collection
 *   2. "Add Items" tab — search / browse library to link items
 */

import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder, faFolderOpen, faStar, faBullseye, faLightbulb,
  faThumbtack, faMicroscope, faPalette, faCamera, faPencil,
  faLink, faMusic, faGamepad, faTrophy, faBookmark, faBriefcase,
  faSearch, faSpinner, faTimes, faPlus, faTrash, faLayerGroup,
  faExclamationTriangle, faBoxOpen, faCheck, faList,
} from '@fortawesome/free-solid-svg-icons';
import useI18n from '../hooks/useI18n';
import { useCollections, Collection } from '../hooks/useCollections';
import { itemsAPI, resolveFileUrl } from '../services/api';
import ItemDetailView from '../components/ItemDetailView';
import PasswordLockModal from '../components/PasswordLockModal';
import '../styles/Collections.css';

// ─── constants ────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, any> = {
  faFolder, faFolderOpen, faStar, faBullseye, faLightbulb,
  faThumbtack, faMicroscope, faPalette, faCamera, faPencil,
  faLink, faMusic, faGamepad, faTrophy, faBookmark, faBriefcase,
};
const ICON_KEYS = Object.keys(ICON_MAP);

const ITEM_TYPES = [
  'document','image','link','note','video','audio',
  'spreadsheet','presentation','report','task',
  'invoice','contract','email','contact','blueprint','code',
];

const DEFAULT_ICON = 'faFolder';
const DEFAULT_COLOR = '#6366f1';

// ─── helpers ──────────────────────────────────────────────────────────────────

function itemCount(col: Collection) { return col.items?.length ?? 0; }

function renderIcon(iconKey: string | undefined, className?: string) {
  const key = iconKey || DEFAULT_ICON;
  const icon = ICON_MAP[key] || faFolder;
  return <FontAwesomeIcon icon={icon} className={className} />;
}

// ─── main component ───────────────────────────────────────────────────────────

const Collections: React.FC = () => {
  const { t } = useI18n();

  const {
    collections,
    loading,
    addCollection,
    updateCollection,
    deleteCollection,
    addItemToCollection,
    removeItemFromCollection,
  } = useCollections();

  // toolbar
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'custom' | 'name' | 'recent' | 'items'>('custom');

  // create / edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Collection | null>(null);
  const [modalIcon, setModalIcon] = useState(DEFAULT_ICON);
  const [modalName, setModalName] = useState('');
  const [modalDesc, setModalDesc] = useState('');

  // detail panel
  const [detail, setDetail] = useState<Collection | null>(null);
  // Tab inside detail: "items" = view existing, "add" = add from library
  const [detailTab, setDetailTab] = useState<'items' | 'add'>('items');

  // manual add form
  const [newItemName, setNewItemName] = useState('');
  const [newItemType, setNewItemType] = useState('document');
  const nameInputRef = useRef<HTMLInputElement>(null);

  // delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);

  // item detail popup (for linked library items)
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [showItemDetail, setShowItemDetail] = useState(false);

  // lock gate for collection items
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockTargetItem, setLockTargetItem] = useState<any | null>(null);

  // search within collection items tab
  const [collectionItemSearch, setCollectionItemSearch] = useState('');

  // library search/browse state
  const [libMode, setLibMode] = useState<'search' | 'browse'>('search');
  const [libSearch, setLibSearch] = useState('');
  const [libResults, setLibResults] = useState<any[]>([]);
  const [libLoading, setLibLoading] = useState(false);
  const [browseItems, setBrowseItems] = useState<any[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseCatFilter, setBrowseCatFilter] = useState('');
  const [browseTextFilter, setBrowseTextFilter] = useState('');
  const [browseFavOnly, setBrowseFavOnly] = useState(false);
  const [browseCondFilter, setBrowseCondFilter] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── debounced library search ──────────────────────────────────────────────

  const searchLibrary = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setLibResults([]);
      setLibLoading(false);
      return;
    }
    setLibLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await itemsAPI.search(query.trim());
        setLibResults(res.data || []);
      } catch {
        setLibResults([]);
      } finally {
        setLibLoading(false);
      }
    }, 350);
  }, []);

  function handleLibSearchChange(value: string) {
    setLibSearch(value);
    searchLibrary(value);
  }

  const loadBrowseItems = useCallback(async () => {
    if (browseItems.length > 0) return;
    setBrowseLoading(true);
    try {
      const res = await itemsAPI.getAll({ take: 200 });
      setBrowseItems(res.data?.items || res.data || []);
    } catch {
      setBrowseItems([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [browseItems.length]);

  // ── add library item to collection ────────────────────────────────────────

  async function handleAddLibraryItem(item: any) {
    if (!liveDetail) return;
    const type = item.category?.name?.toLowerCase() || 'document';
    try {
      await addItemToCollection(liveDetail.id, { name: item.name, type, itemId: item.id });
    } catch (err) {
      console.error('Failed to add item:', err);
    }
  }

  // ── check if an item is already in the collection (uses live data) ────────

  function isItemInCollection(libraryItemId: string): boolean {
    if (!liveDetail) return false;
    return liveDetail.items.some(ci => ci.itemId === libraryItemId);
  }

  // ── filtered / sorted list ────────────────────────────────────────────────

  const visible = collections
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'recent') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      if (sortBy === 'items') return itemCount(b) - itemCount(a);
      return a.sortOrder - b.sortOrder;
    });

  // ── handlers ──────────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setModalIcon(DEFAULT_ICON);
    setModalName('');
    setModalDesc('');
    setShowModal(true);
  }

  function handleDelete(e: React.MouseEvent, col: Collection) {
    e.stopPropagation();
    setDeleteTarget(col);
  }

  function confirmDelete() {
    if (deleteTarget) {
      deleteCollection(deleteTarget.id);
      if (detail?.id === deleteTarget.id) setDetail(null);
      setDeleteTarget(null);
    }
  }

  function openDetail(col: Collection) {
    setDetail(col);
    setDetailTab('items');
    setCollectionItemSearch('');
    setNewItemName('');
    setNewItemType('document');
    setLibMode('search');
    setLibSearch('');
    setLibResults([]);
    setBrowseCatFilter('');
    setBrowseTextFilter('');
    setBrowseFavOnly(false);
    setBrowseCondFilter('');
  }

  const liveDetail = detail ? collections.find(c => c.id === detail.id) ?? null : null;

  function handleAddItem() {
    if (!liveDetail || !newItemName.trim()) return;
    addItemToCollection(liveDetail.id, { name: newItemName.trim(), type: newItemType });
    setNewItemName('');
    nameInputRef.current?.focus();
  }

  function handleRemoveItem(collectionItemId: string) {
    if (!liveDetail) return;
    removeItemFromCollection(liveDetail.id, collectionItemId);
  }

  async function handleViewCollectionItem(item: any) {
    if (!item.itemId) return;
    try {
      const res = await itemsAPI.getById(item.itemId);
      const fetchedItem = res.data;
      if (fetchedItem.isLocked) {
        setLockTargetItem(fetchedItem);
        setShowLockModal(true);
      } else {
        setDetailItem(fetchedItem);
        setShowItemDetail(true);
      }
    } catch {
      console.error('Could not load item details');
    }
  }

  function handleCollectionLockSuccess() {
    setShowLockModal(false);
    if (lockTargetItem) {
      setDetailItem(lockTargetItem);
      setShowItemDetail(true);
      setLockTargetItem(null);
    }
  }

  // ── Create/Edit Modal (called as function, not component, to preserve input focus) ──

  function CreateEditModal() {
    function save() {
      if (!modalName.trim()) return;
      if (editing) {
        updateCollection(editing.id, { name: modalName.trim(), description: modalDesc.trim(), icon: modalIcon });
      } else {
        addCollection({ name: modalName.trim(), description: modalDesc.trim(), color: DEFAULT_COLOR, icon: modalIcon });
      }
      setShowModal(false);
    }

    function handleKey(e: React.KeyboardEvent) {
      if (e.key === 'Enter') save();
      if (e.key === 'Escape') setShowModal(false);
    }

    return createPortal(
      <div className="ecol-overlay" onClick={() => setShowModal(false)}>
        <div className="ecol-modal-wrap" onClick={e => e.stopPropagation()} onKeyDown={handleKey}>
          <p className="ecol-modal-title">
            {editing ? t('collections.editCollection') : t('collections.createCollection')}
          </p>

          <div className="ecol-form-group">
            <label className="ecol-form-label">{t('collections.icon') || 'Icon'}</label>
            <div className="ecol-icon-grid">
              {ICON_KEYS.map(ic => (
                <button
                  key={ic}
                  type="button"
                  className={`ecol-icon-btn${modalIcon === ic ? ' selected' : ''}`}
                  onClick={() => setModalIcon(ic)}
                >
                  <FontAwesomeIcon icon={ICON_MAP[ic]} />
                </button>
              ))}
            </div>
          </div>

          <div className="ecol-form-group">
            <label className="ecol-form-label">{t('collections.name')}</label>
            <input
              className="ecol-form-input"
              value={modalName}
              onChange={e => setModalName(e.target.value)}
              placeholder={t('collections.name')}
              autoFocus
            />
          </div>

          <div className="ecol-form-group">
            <label className="ecol-form-label">{t('collections.description')}</label>
            <textarea
              className="ecol-form-input"
              value={modalDesc}
              onChange={e => setModalDesc(e.target.value)}
              placeholder={t('collections.description')}
              rows={3}
            />
          </div>

          <div className="ecol-modal-actions">
            <button className="ecol-btn-cancel" type="button" onClick={() => setShowModal(false)}>
              {t('action.cancel')}
            </button>
            <button className="ecol-btn-save" type="button" onClick={save} disabled={!modalName.trim()}>
              {editing ? t('common.save') || 'Save' : t('collections.createCollection')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── render library item row (used in search & browse) ─────────────────────

  function renderLibItem(item: any) {
    const thumb = item.images?.find((img: any) => img.isPrimary)?.url
      || item.images?.[0]?.url
      || item.imageUrl || null;
    const alreadyAdded = isItemInCollection(item.id);

    return (
      <div className={`ecol-lib-result-row${alreadyAdded ? ' added' : ''}`} key={item.id}>
        {thumb ? (
          <img className="ecol-lib-result-img" src={resolveFileUrl(thumb) || thumb} alt="" />
        ) : (
          <div className="ecol-lib-result-no-img">
            <FontAwesomeIcon icon={faBoxOpen} />
          </div>
        )}
        <div className="ecol-lib-result-info">
          <span className="ecol-lib-result-name">{item.name}</span>
          <div className="ecol-lib-result-meta">
            {item.category?.name && <span className="ecol-lib-result-cat">{item.category.name}</span>}
            {item.condition && item.condition !== 'GOOD' && (
              <span className="ecol-lib-result-cond">
                {item.condition.toLowerCase().replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>
        <button
          className={`ecol-lib-add-btn${alreadyAdded ? ' done' : ''}`}
          type="button"
          onClick={() => !alreadyAdded && handleAddLibraryItem(item)}
          disabled={alreadyAdded}
        >
          <FontAwesomeIcon icon={alreadyAdded ? faCheck : faPlus} />
        </button>
      </div>
    );
  }

  // ── Detail Modal (called as function, not component, to preserve input focus) ──

  function DetailModal() {
    if (!liveDetail) return null;

    return createPortal(
      <div className="ecol-overlay" onClick={() => setDetail(null)}>
        <div className="ecol-detail" onClick={e => e.stopPropagation()}>
          {/* header */}
          <div className="ecol-detail-head">
            <div className="ecol-detail-icon">{renderIcon(liveDetail.icon)}</div>
            <div className="ecol-detail-head-info">
              <p className="ecol-detail-name">{liveDetail.name}</p>
              {liveDetail.description && (
                <p className="ecol-detail-desc">{liveDetail.description}</p>
              )}
              <p className="ecol-detail-count">
                {itemCount(liveDetail)} {itemCount(liveDetail) === 1 ? t('collections.item') || 'item' : t('collections.items') || 'items'}
              </p>
            </div>
            <button className="ecol-detail-close" type="button" onClick={() => setDetail(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Tab bar */}
          <div className="ecol-detail-tabs">
            <button
              className={`ecol-detail-tab${detailTab === 'items' ? ' active' : ''}`}
              onClick={() => setDetailTab('items')}
            >
              <FontAwesomeIcon icon={faList} />
              <span>{t('collections.itemsTab') || 'Items'}</span>
              {liveDetail.items.length > 0 && (
                <span className="ecol-tab-count">{liveDetail.items.length}</span>
              )}
            </button>
            <button
              className={`ecol-detail-tab${detailTab === 'add' ? ' active' : ''}`}
              onClick={() => { setDetailTab('add'); if (libMode === 'browse') loadBrowseItems(); }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{t('collections.addItemsTab') || 'Add Items'}</span>
            </button>
          </div>

          {/* ═══ ITEMS TAB ═══ */}
          {detailTab === 'items' && (
            <div className="ecol-items-view">
              {liveDetail.items.length === 0 ? (
                <div className="ecol-items-empty">
                  <FontAwesomeIcon icon={faBoxOpen} className="ecol-items-empty-icon" />
                  <p>{t('collections.noItems') || 'No items in this collection'}</p>
                  <button className="ecol-btn-save" onClick={() => setDetailTab('add')}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                    {t('collections.addItemsTab') || 'Add Items'}
                  </button>
                </div>
              ) : (
                <>
                  {liveDetail.items.length > 3 && (
                    <div className="ecol-items-search" style={{ padding: '0 0 10px 0' }}>
                      <input
                        type="text"
                        placeholder={t('collections.searchItems') || 'Search items...'}
                        value={collectionItemSearch}
                        onChange={(e) => setCollectionItemSearch(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)',
                          color: '#e2e8f0', fontSize: '0.9rem', outline: 'none',
                        }}
                      />
                    </div>
                  )}
                  <div className="ecol-item-list">
                  {liveDetail.items
                    .filter(item => {
                      if (!collectionItemSearch.trim()) return true;
                      const q = collectionItemSearch.toLowerCase();
                      return item.name.toLowerCase().includes(q) ||
                        (item.type && item.type.toLowerCase().includes(q));
                    })
                    .map(item => {
                    const libItem = browseItems.find((b: any) => b.id === item.itemId)
                      || libResults.find((b: any) => b.id === item.itemId);
                    const thumb = libItem?.images?.find((img: any) => img.isPrimary)?.url
                      || libItem?.images?.[0]?.url
                      || libItem?.imageUrl || null;
                    const resolvedThumb = thumb ? (resolveFileUrl(thumb) || thumb) : null;

                    return (
                      <div
                        className={`ecol-item-row${item.itemId ? ' clickable' : ''}`}
                        key={item.id}
                        onClick={() => item.itemId && handleViewCollectionItem(item)}
                      >
                        <div className="ecol-item-row-left">
                          {resolvedThumb ? (
                            <img className="ecol-item-thumb" src={resolvedThumb} alt="" />
                          ) : (
                            <span className={`ecol-type-badge ecol-type-${item.type}`}>
                              {(() => { const k = `collections.type.${item.type}`; const v = t(k); return v === k ? (item.type.charAt(0).toUpperCase() + item.type.slice(1)) : v; })()}
                            </span>
                          )}
                          <span className="ecol-item-name">{item.name}</span>
                          {item.itemId && (
                            <span className="ecol-item-link-badge">
                              <FontAwesomeIcon icon={faLink} />
                            </span>
                          )}
                        </div>
                        <button
                          className="ecol-item-remove"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
                          title={t('common.delete') || 'Remove'}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    );
                  })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ═══ ADD ITEMS TAB ═══ */}
          {detailTab === 'add' && (
            <div className="ecol-add-view">
              {/* Library search/browse */}
              <div className="ecol-lib-search-section">
                <div className="ecol-lib-header">
                  <span className="ecol-lib-title">
                    <FontAwesomeIcon icon={faBoxOpen} />
                    {t('collections.addFromLibrary') || 'Add from Library'}
                  </span>
                  <div className="ecol-lib-mode-tabs">
                    <button
                      className={`ecol-lib-tab${libMode === 'search' ? ' active' : ''}`}
                      onClick={() => setLibMode('search')}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                      {t('collections.searchTab') || 'Search'}
                    </button>
                    <button
                      className={`ecol-lib-tab${libMode === 'browse' ? ' active' : ''}`}
                      onClick={() => { setLibMode('browse'); loadBrowseItems(); }}
                    >
                      <FontAwesomeIcon icon={faBoxOpen} />
                      {t('collections.browseAllTab') || 'Browse All'}
                    </button>
                  </div>
                </div>

                {/* Search mode */}
                {libMode === 'search' && (
                  <>
                    <div className="ecol-lib-search-row">
                      <span className="ecol-lib-search-icon"><FontAwesomeIcon icon={faSearch} /></span>
                      <input
                        className="ecol-lib-search-input"
                        value={libSearch}
                        onChange={e => handleLibSearchChange(e.target.value)}
                        placeholder={t('collections.searchLibraryPlaceholder') || 'Search your items...'}
                        autoFocus
                      />
                      {libLoading && <FontAwesomeIcon icon={faSpinner} spin className="ecol-lib-spin" />}
                      {libSearch && !libLoading && (
                        <button className="ecol-lib-clear-btn" onClick={() => { setLibSearch(''); setLibResults([]); }}>
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                    <div className="ecol-lib-results">
                      {libResults.map(item => renderLibItem(item))}
                    </div>
                    {libSearch.trim().length >= 2 && !libLoading && libResults.length === 0 && (
                      <p className="ecol-lib-no-results">{t('collections.noLibraryResults') || 'No items found'}</p>
                    )}
                    {!libSearch && (
                      <p className="ecol-lib-hint">{t('collections.searchHint') || 'Type at least 2 characters to search'}</p>
                    )}
                  </>
                )}

                {/* Browse mode */}
                {libMode === 'browse' && (
                  <>
                    <div className="ecol-lib-search-row">
                      <span className="ecol-lib-search-icon"><FontAwesomeIcon icon={faSearch} /></span>
                      <input
                        className="ecol-lib-search-input"
                        value={browseTextFilter}
                        onChange={e => setBrowseTextFilter(e.target.value)}
                        placeholder={t('collections.filterItems') || 'Filter items...'}
                      />
                      {browseLoading && <FontAwesomeIcon icon={faSpinner} spin className="ecol-lib-spin" />}
                    </div>

                    {(() => {
                      const cats = [...new Set(browseItems.map((i: any) => i.category?.name).filter(Boolean))] as string[];
                      const conditions = [...new Set(browseItems.map((i: any) => i.condition).filter((c: any) => c && c !== 'N/A'))] as string[];
                      const filtered = browseItems.filter((i: any) => {
                        const matchCat = !browseCatFilter || i.category?.name === browseCatFilter;
                        const matchText = !browseTextFilter || i.name.toLowerCase().includes(browseTextFilter.toLowerCase());
                        const matchFav = !browseFavOnly || i.isFavorite;
                        const matchCond = !browseCondFilter || i.condition === browseCondFilter;
                        return matchCat && matchText && matchFav && matchCond;
                      });

                      return (
                        <>
                          {cats.length > 0 && (
                            <div className="ecol-lib-cat-chips">
                              <button
                                className={`ecol-lib-cat-chip${!browseCatFilter ? ' active' : ''}`}
                                onClick={() => setBrowseCatFilter('')}
                              >{t('collections.allCategories') || 'All'}</button>
                              {cats.map(cat => (
                                <button
                                  key={cat}
                                  className={`ecol-lib-cat-chip${browseCatFilter === cat ? ' active' : ''}`}
                                  onClick={() => setBrowseCatFilter(cat)}
                                >{cat}</button>
                              ))}
                            </div>
                          )}

                          <div className="ecol-lib-extra-filters">
                            <button
                              className={`ecol-lib-cat-chip${browseFavOnly ? ' active' : ''}`}
                              onClick={() => setBrowseFavOnly(!browseFavOnly)}
                            >
                              <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />
                              {t('nav.favorites') || 'Favorites'}
                            </button>
                            {conditions.length > 0 && (
                              <select
                                className="ecol-lib-cond-filter"
                                value={browseCondFilter}
                                onChange={e => setBrowseCondFilter(e.target.value)}
                              >
                                <option value="">{t('collections.allConditions') || 'All conditions'}</option>
                                {conditions.map(c => (
                                  <option key={c} value={c}>{c.replace(/_/g, ' ').toLowerCase()}</option>
                                ))}
                              </select>
                            )}
                          </div>

                          <div className="ecol-lib-results">
                            {!browseLoading && filtered.map(item => renderLibItem(item))}
                          </div>
                          {!browseLoading && filtered.length === 0 && browseItems.length > 0 && (
                            <p className="ecol-lib-no-results">{t('collections.noLibraryResults') || 'No items found'}</p>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* manual add */}
              <div className="ecol-divider">
                <span>{t('collections.orAddManually') || 'or add manually'}</span>
              </div>

              <div className="ecol-add-item-form">
                <div className="ecol-add-item-row">
                  <select
                    className="ecol-type-select"
                    value={newItemType}
                    onChange={e => setNewItemType(e.target.value)}
                  >
                    {ITEM_TYPES.map(tp => (
                      <option key={tp} value={tp}>{t(`collections.type.${tp}`) || tp}</option>
                    ))}
                  </select>
                  <input
                    ref={nameInputRef}
                    className="ecol-item-name-input"
                    value={newItemName}
                    onChange={e => setNewItemName(e.target.value)}
                    placeholder={t('collections.itemNamePlaceholder') || 'Item name...'}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddItem(); }}
                  />
                  <button
                    className="ecol-add-item-btn"
                    type="button"
                    onClick={handleAddItem}
                    disabled={!newItemName.trim()}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="ecol-page">
      {/* header */}
      <div className="ecol-header">
        <div className="ecol-header-title-wrap">
          <div className="ecol-header-icon">
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div>
            <h1>{t('collections.title')}</h1>
            <p className="ecol-header-sub">{t('collections.subtitle')}</p>
          </div>
        </div>
        <button className="ecol-btn-new" type="button" onClick={openCreate}>
          <FontAwesomeIcon icon={faPlus} /> {t('collections.newCollection')}
        </button>
      </div>

      {/* toolbar */}
      <div className="ecol-toolbar">
        <div className="ecol-search-wrap">
          <span className="ecol-search-icon"><FontAwesomeIcon icon={faSearch} /></span>
          <input
            className="ecol-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('collections.searchPlaceholder')}
          />
        </div>
        <select
          className="ecol-sort-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value as typeof sortBy)}
        >
          <option value="custom">{t('collections.customOrder')}</option>
          <option value="name">{t('collections.name')}</option>
          <option value="recent">{t('collections.updated')}</option>
          <option value="items">{t('collections.items')}</option>
        </select>
      </div>

      {/* grid */}
      {loading ? (
        <div className="ecol-empty">
          <div className="ecol-empty-icon"><FontAwesomeIcon icon={faSpinner} spin /></div>
          <p>{t('collections.loading')}</p>
        </div>
      ) : (
        <div className="ecol-grid">
          {visible.map(col => (
            <div key={col.id} className="ecol-card" onClick={() => openDetail(col)}>
              <div className="ecol-card-top">
                <div className="ecol-card-icon-wrap">{renderIcon(col.icon)}</div>
                <button
                  className="ecol-card-delete"
                  type="button"
                  onClick={e => handleDelete(e, col)}
                  title={t('common.delete') || 'Delete'}
                ><FontAwesomeIcon icon={faTrash} /></button>
              </div>
              <div className="ecol-card-name">{col.name}</div>
              {col.description && <div className="ecol-card-desc">{col.description}</div>}
              <div className="ecol-card-count">
                {itemCount(col)} {itemCount(col) === 1 ? (t('collections.item') || 'item') : (t('collections.items') || 'items')}
              </div>
            </div>
          ))}

          <div className="ecol-card-add" onClick={openCreate}>
            <div className="ecol-card-add-icon"><FontAwesomeIcon icon={faPlus} /></div>
            <div className="ecol-card-add-label">{t('collections.newCollection')}</div>
          </div>
        </div>
      )}

      {visible.length === 0 && !loading && search && (
        <div className="ecol-empty">
          <div className="ecol-empty-icon"><FontAwesomeIcon icon={faSearch} /></div>
          <h3>{t('collections.noMatchingCollections')}</h3>
          <p>{t('collections.tryDifferentSearch')}</p>
        </div>
      )}

      {visible.length === 0 && !loading && !search && collections.length === 0 && (
        <div className="ecol-empty">
          <div className="ecol-empty-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
          <h3>{t('collections.noCollectionsYet')}</h3>
          <p>{t('collections.noCollectionsDesc')}</p>
        </div>
      )}

      {showModal && CreateEditModal()}
      {liveDetail && DetailModal()}

      {/* Delete confirmation */}
      {deleteTarget && createPortal(
        <div className="ecol-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ecol-confirm-wrap" onClick={e => e.stopPropagation()}>
            <div className="ecol-confirm-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
            <p className="ecol-confirm-title">{t('collections.deleteConfirm')}</p>
            <p className="ecol-confirm-desc">{t('collections.deleteConfirmDesc') || 'Items in this collection will not be deleted.'}</p>
            <div className="ecol-modal-actions">
              <button className="ecol-btn-cancel" type="button" onClick={() => setDeleteTarget(null)}>
                {t('action.cancel')}
              </button>
              <button className="ecol-btn-danger" type="button" onClick={confirmDelete}>
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
                {t('action.delete')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Item Detail popup for linked library items */}
      {detailItem && (
        <ItemDetailView
          item={detailItem}
          isOpen={showItemDetail}
          onClose={() => { setShowItemDetail(false); setDetailItem(null); }}
          onEdit={() => {}}
          onDelete={() => {}}
          onToggleFavorite={() => {}}
        />
      )}

      {/* Lock Gate Modal for locked items in collections */}
      {showLockModal && lockTargetItem && (
        <PasswordLockModal
          itemId={lockTargetItem.id}
          itemName={lockTargetItem.name}
          isLocked={true}
          mode="verify"
          onClose={() => { setShowLockModal(false); setLockTargetItem(null); }}
          onSuccess={handleCollectionLockSuccess}
        />
      )}
    </div>
  );
};

export default Collections;
